import { useState, useEffect } from "react"
import { merge } from "lodash"
import { Grid, SpaceBetween, TextContent } from "@cloudscape-design/components"
import { upsert, useQueryData } from "../hooks/useQueryData"
import { ConvertDate, NowDB, TodayDB } from "../utils"
import ComboInput from "../widgets/ComboInput"
import LabeledInput from "../widgets/LabeledInput"
import SelectInput from "../widgets/SelectInput"
import { CurrentUser } from '../services/SessionService'
import { sendEmailTo } from "../services/Email.ts"
import { Notify } from '../services/Notify'
import SegmentedInput from "../widgets/SegmentedInput"
import Select from '../widgets/Select.tsx'
import { TemplateRecord } from '../models/Template.ts'

import FancyTextEditor, { setContentFieldsSubject, getContent } from "../widgets/FancyTextEditor.tsx"
import { fetchData } from "../services/MiddleTierData.ts"

export const EmailTaskSettings = {size: "max", title: "Email/Fax Task"}

export default function EmailTask({values, setMessage, execRef}: {values: any, setMessage: (s: string, t: string) => void, execRef: React.MutableRefObject<() => Promise<string>>}) {
    setMessage("Click Ok to send this email/fax. Click Cancel to close this dialog without sending.", "info")
    const user = CurrentUser()

    const defaultEntries: any = {
      from: values?.From ?? 'info@legal25.com',
      to: '', cc: '', bcc: '', // values?.Contact?.Email ?? '', cc: '', bcc: '',
      fax: values?.Case?.Details?.FirstPartyAdjuster?.Fax ?? '',
      subject: 'A message from ' + (user?.fullName ?? 'info@legal25.com') + ' with Legal 25',
      body: '',
      template: values?.Template ?? 'None',
    }
    const [entries, setEntries] = useState(merge({}, defaultEntries))
    const [mode, setMode] = useState(values?.Mode ?? 'Email') // Default to Email mode
    const [showCC, setShowCC] = useState(false)
    const [ category, setCategory ] = useState<string>("All Categories")
    const [ template, setTemplate ] = useState<string>("")
    const [ categories, setCategories ] = useState<string[]>([])
    const [ allTemplates, setAllTemplates ] = useState<TemplateRecord[]>([])
    const [ filtered, setFiltered ] = useState<TemplateRecord[]>([])

    // Load templates from the server
    const { data, isLoading } = useQueryData('TextTemplate')
    
    useEffect(() => {
      setAllTemplates(data?.results ?? [])
    }, [data])
   
    useEffect(() => {
      let catSet = new Set(CategorySort)
      catSet = catSet.union(new Set(allTemplates.map((t: TemplateRecord) => t.TemplateType.trim())))
      setCategories([...catSet])
    }, [allTemplates])

    useEffect(() => {
      setFiltered(category === "All Categories" ? allTemplates : allTemplates.filter((t: TemplateRecord) => t.TemplateType == category))
    }, [categories, category])

    function validate(value: string, label: string): boolean {
        const thisValid: boolean = value?.length > 0
        if (!thisValid) setMessage("Please provide values for all required fields", "error")
        return thisValid
    }

    const handleChange = (newValues: any) => {
        if (newValues?.template) {
          setTemplate(newValues.template)
        } else setEntries(merge({}, entries, newValues))
    }

    async function execute(): Promise<string> {
        if (mode === 'Fax') {
            // Validate all fields
            const faxNum = (entries?.fax ?? "").replace(/[^0-9]/g, "")

            if (!faxNum) return "Please provide a Fax number"
            entries.from = 'fax@legal25.com'
            entries.to = entries.faxNum + "@metrofax.com"
        }

        // Validate all fields
        if (!entries?.from) return "ERROR: Please provide a From address"
        if (!entries?.to) return "ERROR: Please provide a To address"
        if (!entries?.subject) return "ERROR: Please provide a Subject"

        // Validate the body
        const body = getContent()
        if (!body || body.trim().length === 0) {return "ERROR: Please provide a body for this message"}
        if (body.includes("{{")) {return "ERROR: Please replace all template fields in the body of this message"}
        if (body.includes("***** Replace *****")) {return "ERROR: Please replace the body for this message"}

        let result: string = ""
        result = await sendEmailTo(
            entries.from,
            entries.to,
            entries.cc,
            entries.bcc,
            entries.subject,
            "Your application does not support HTML emails",
            body,
            values.Documents,
        )

        if (result && result !== "") return result

        // If we reach here, the email was sent successfully
        Notify('success', `Email sent to ${entries.to} with subject "${entries.subject}"`)
           
        // Create Communication record
        upsert('Communication', {
            Type: mode, 
            Subject: entries.Subject,
            ToName: values?.Contact?.FirstName + " " + values?.Contact?.LastName,
            ToAddress: entries.To,
            Result: entries.Result,
            Body: entries.Body, 
            CaseId: values?.Case?._id, 
            ContactId: values?.Contact?._id,
        })

        // Create Activity
        upsert('Activity', {
            ForId: "Case:" + values?.Case?._id,
            CaseId: values?.Case?._id,
            Type: values?.Activity ?? "Contact",
            Description: entries.template + " " + mode + " to " + entries.to,
            On: NowDB(),
        })

        return '' // No error. Successful execution
    }
    execRef.current = execute
    // debugger

    let t = ""

    async function setSelectedTemplate(templateName: string) {
      setTemplate(templateName)
      let template = allTemplates.find((t: any) => t.Name === templateName)
      if (!template) { return }

      t = template?.Template
      if (t) {
          // Replace {{Weekly.Update}} field
          if (t.includes('{{Weekly.Update}}')) {
            let details = ""
            const results = await fetchData('CaseSummary', { Where: [{ kind: 'equals', field: 'CaseId', value: values?.Case?._id }] })
            const cs = results?.data?.[0]
            if (!cs) {
              details = "Case Summary not found for this case. Please contact your account manager for help."
              setMessage("Case Summary not found for this case.", "warning")
              return
            } else {
              details = "Case: " + cs.Name + "<br/>" + cs?.ProgressSummary + "<br/>Statute of Limitations: " + ConvertDate(cs?.SOL)
              t = t.replaceAll('{{Weekly.Update}}', details)
            }
          }

          // Replace all {{fieldName}} with the value from values
          const result = setContentFieldsSubject(t, values)
          setEntries(merge({}, entries, { subject: result.subject, template: templateName }))
          console.log("Template set", { subject: result.subject, html: result.html, values: values })
        }
    }

    useEffect(() => {
      // Set the template if it exists in the values
      if (values?.Template) {
          setTemplate(values.Template)
      } else {
          // If no template, set default subject
          // setEntries(merge({}, entries, { subject: 'A message from ' + (user?.fullName ?? 'support@legal25.com') + ' with Gaylord Claims' }))
      }}, [allTemplates, values?.Template])
    
    const contactName = !values?.Contact?.FirstName ? "None Selected" : values?.Contact?.FirstName + " " + values?.Contact?.LastName

    return (
        <>
        <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
            {/* <ToggleButton
              onChange={ ({ detail }) => setShowCC(detail.pressed) }
              pressed={showCC}
              iconName="angle-right"
              pressedIconName="angle-down"
            /> */}
            <SpaceBetween direction="vertical" size="s">
              <SegmentedInput readOnly={false} validate={validate}
                  label="Mode"
                  onChange= {(v) => setMode(v)}
                  value={mode}
                  options="Email;Fax"
              />
              {mode === 'Email' ? (<>
              <ComboInput label="From:" value={entries?.from} validate={validate} readOnly={false}
                options={[user.email ?? '', 'info@legal25.com', 'support@legal25.com']}
                onChange={(v) => handleChange({ from: v })} />
              <ComboInput label="To:" value={entries?.to} validate={validate} readOnly={false}
                options={[values?.Contact?.Email ?? 'Missing Client Email', 
                  values?.Case?.Details?.FirstPartyAdjuster?.Email ?? 'Missing First Party Email', 
                  values?.Case?.Details?.ThirdPartyAdjuster?.Email ?? 'Missing Third Party Email', 
                  values?.Contact?.EmergencyContact?.Email ?? 'Missing Emergency Email', 
                  values?.Case?.Details?.AtFault?.Email ?? 'Missing At Fault Email', 
                ]}
                onChange={(v) => handleChange({ to: v })} />
                </>) : (
              <ComboInput label="Fax Number" value={entries?.fax} validate={validate} readOnly={false}
                options={[
                  values?.Case?.Details?.FirstPartyAdjuster?.Fax ?? 'Missing First Party Fax', 
                  values?.Case?.Details?.ThirdPartyAdjuster?.Fax ?? 'Missing Third Party Fax', 
                ]}
                onChange={(v) => handleChange({ fax: v })} />
              )}

              {/* <LabeledInput label="CC" value={entries?.cc ?? ''} readOnly={false}
                onChange={({ detail } : {detail: any}) => handleChange({ cc: detail.value })} />
              <LabeledInput label="BCC" value={entries?.bcc ?? ''} readOnly={false}
                onChange={({ detail } : {detail: any}) => handleChange({ bcc: detail.value })} /> */}
              <SelectInput label="Template" value={category ?? ""} validate={validate} readOnly={false} 
                options={categories}
                onChange={(v: any) => setCategory(v.detail.value)}
              />
              <SelectInput label="" value={template ?? ''} validate={validate} readOnly={false} 
                onChange={ ({detail}: {detail: any}) => setSelectedTemplate(detail.value) }
                options={filtered.map((t: TemplateRecord) => t.Name)} 
              />

              <TextContent>
                <hr data-text="Case"/>
                {/* <CaseSelector caseId={values?.thisCase?.objectId} caseName={values?.thisCase?.Name}/> */}
                <p>{values?.Case?.Name ?? "None Selected"}</p>
                <br/>
                <hr data-text="Contact"/>
                <p>{contactName}</p>
                <br/>
                <hr data-text="Files"/>
                {values?.Documents?.map((doc: any) => (
                  <><span key={doc?.name}>{doc?.name}</span><br/></>
                ))}
              </TextContent>
          </SpaceBetween>
          <SpaceBetween direction="vertical" size="s">
            <LabeledInput label="Subject" value={entries?.subject} validate={validate} readOnly={false}
              onChange={ ({detail}: {detail: any}) => handleChange({ subject: detail.value })} />
            {/* <LabeledTextarea rows={22} label="" value={entries?.body} readOnly={false} validate={validate}
              onChange={ ({detail}: {detail: any}) => handleChange({ body: detail.value }) }/> */}
            <FancyTextEditor editable={true} />
          </SpaceBetween>
        </Grid>
        </>
      )
    }

    
const CategorySort = [
  "All Categories",
  "Onboarding",
  "Investigation and Evidence",
  "Property Damage",
  "Insurance: 1st Party",
  "Insurance: 3rd Party",
  "Insurance Follow Ups",
  "General Communication",
  "Check-Ins and Treatment",
  "Completing Treatment and Building Demand",
  "Demand and Settlement",
  "Signatures and Documents",
  "Litigation - WA",
]